import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/outline";
import { CheckIcon } from "@heroicons/react/solid";
import { useParse } from "@refinedev/core";
import { CreateEventStep, EventType } from "interfaces";
import { Link } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { CREATE_EVENT_STEPS } from "utills/constants";

const StatusCircle = ({
  step,
  currentStep,
  maxStep,
}: {
  step: number;
  currentStep: number;
  maxStep: number;
}) => {
  return (
    <div className="shrink-0">
      <div
        className={twMerge(
          "relative mt-0.5 w-5 h-5 border-solid border-2 rounded-full overflow-hidden shrink-0",
          maxStep >= step - 1 || currentStep === step
            ? "border-brand-500"
            : "border-gray-500"
        )}
      >
        {currentStep === step ? (
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-brand-500 w-2.5 h-2.5 flex-1 rounded-full"></div>
        ) : maxStep < step ? (
          <div className="bg-white w-2.5 h-2.5 flex-1 rounded-full"></div>
        ) : (
          <CheckIcon className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5 text-brand-600 stroke-brand-600" />
        )}
      </div>
    </div>
  );
};

export const StepsMenu = ({
  currentStep = 0,
  eventType,
  isEdit = true,
}: {
  currentStep?: number;
  eventType: EventType;
  isEdit?: boolean;
}) => {
  const parse = useParse();
  const { params } = parse();

  const getBackLinkPath = () => {
    const step = Number(params?.step ?? 0);
    const baseUrl = `/events/${
      eventType === "sport" ? "sports" : "attractions"
    }`;

    if (step <= CreateEventStep.BUILD_EVENT_PAGE) {
      return baseUrl;
    }

    if (params?.id) {
      const editPath = isEdit ? "/edit" : "";
      return `${baseUrl}/${params.id}${editPath}?step=${step - 1}`;
    }

    return `${baseUrl}/create`;
  };
  const getNextLinkPath = () => {
    const step = Number(params?.step ?? 0);
    const baseUrl = `/events/${
      eventType === "sport" ? "sports" : "attractions"
    }`;

    if (step >= CreateEventStep.ADDITIONAL_SETUP) {
      return baseUrl;
    }

    if (params?.id) {
      const editPath = isEdit ? "/edit" : "";
      return `${baseUrl}/${params.id}${editPath}?step=${step + 1}`;
    }

    return `${baseUrl}/create`;
  };

  return (
    <div className="max-w-full box-border">
      <div className="lg:hidden">
        <div className="bg-white w-full flex items-center justify-between px-2 py-2 shadow-sm">
          {params?.id ? (
            <Link to={getBackLinkPath()}>
              <div className="relative size-6 rounded-full bg-magnolia-500">
                <ChevronLeftIcon className="size-3 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
              </div>
            </Link>
          ) : (
            <div></div>
          )}
          <p className="font-medium text-sm">
            {
              CREATE_EVENT_STEPS.find(
                (step) =>
                  step.number ===
                  (params?.id && params?.step
                    ? Number(params?.step)
                    : Number(CreateEventStep.BUILD_EVENT_PAGE))
              )?.title
            }
          </p>
          {Number(params?.step) <= currentStep && params?.id ? (
            <Link to={getNextLinkPath()}>
              <div className="relative size-6 rounded-full bg-magnolia-500">
                <ChevronRightIcon className="size-3 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
              </div>
            </Link>
          ) : (
            <div></div>
          )}
        </div>
      </div>
      <div className="hidden lg:block">
        <div className="flex flex-row items-center justify-center w-auto mx-auto">
          {CREATE_EVENT_STEPS.map((step, index) => (
            <div key={index} className="flex items-center">
              {step.number <= currentStep + 1 && params?.id ? (
                <Link
                  to={`/events/${
                    eventType === "sport" ? "sports" : "attractions"
                  }/${
                    params?.id
                      ? `${params?.id}${isEdit ? "/edit" : ""}?step=${
                          step.number
                        }`
                      : "create"
                  }`}
                >
                  <div
                    className={twMerge(
                      "flex flex-row items-center gap-x-2.5 px-4 py-4 sm:px-6 hover:cursor-pointer select-none"
                    )}
                  >
                    <StatusCircle
                      step={step.number}
                      currentStep={Number(params?.step ?? 1)}
                      maxStep={currentStep}
                    />
                    <div>
                      <p className="font-medium shrink-0 text-sm xl:text-base">
                        {step.title}
                      </p>
                    </div>
                  </div>
                </Link>
              ) : (
                <div>
                  <div
                    className={twMerge(
                      "flex flex-row items-start gap-x-2.5 px-2 py-4 sm:px-6 hover:cursor-pointer hover:bg-magnolia-400 select-none"
                    )}
                  >
                    <StatusCircle
                      step={step.number}
                      currentStep={Number(params?.step ?? 1)}
                      maxStep={currentStep}
                    />
                    <div>
                      <p className="font-medium">{step.title}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Connecting Line - only show between steps */}
              {index < CREATE_EVENT_STEPS.length - 1 ? (
                <div className="flex items-center w-18 mx-1">
                  <div
                    className={`w-full h-[2px] rounded-full transition-all duration-500 ${
                      step.number <= currentStep
                        ? "bg-brand-500"
                        : "bg-gray-200"
                    }`}
                  ></div>
                </div>
              ) : (
                <></>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
